import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import PrayerForm from "@/components/PrayerForm";
import PhotoGallery from "@/components/PhotoGallery";
import { INITIAL_EPISODES, PodcastEpisode } from "@/data/podcasts";
import { FeaturedEpisodes } from "@/components/FeaturedEpisodes";
import { PodcastGuestForm } from "@/components/PodcastGuestForm";
import { getStoredEpisodes } from "@/lib/podcastStorage";
import SEOHead from "@/components/SEOHead";

const Podcast = () => {
  const [episodes, setEpisodes] = useState<PodcastEpisode[]>(INITIAL_EPISODES);
  const [activeRecentEpisode, setActiveRecentEpisode] =
    useState<PodcastEpisode>(INITIAL_EPISODES[0]);

  useEffect(() => {
    const stored = getStoredEpisodes();
    const seen = new Set<string>();
    const deduped = stored.filter((e) => {
      if (seen.has(e.id)) return false;
      seen.add(e.id);
      return true;
    });
    setEpisodes(deduped);
    const recent = deduped.find((e) => e.isRecent) || deduped[0];
    if (recent) setActiveRecentEpisode(recent);
  }, []);

  const handleSelectEpisode = (ep: PodcastEpisode) => {
    setActiveRecentEpisode(ep);
    const element = document.getElementById("latest");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <>
      <SEOHead
        title="Between Me & You Podcast with Ieshia Danielle"
        description="Honest conversations about emotions, unspoken trauma, healing, identity, and the God who meets us in the middle of it all. Watch or listen on your platform."
        canonical="https://iamieshiadanielle.com/podcast"
        ogImage="https://drive.google.com/thumbnail?id=1OWUsgK-syRGB8YS1pmfNq5bX_qreJmd2&sz=w1800"
        schemaJson={[
          {
            "@type": "WebPage",
            "@id": "https://iamieshiadanielle.com/podcast#webpage",
            url: "https://iamieshiadanielle.com/podcast",
            name: "Between Me & You Podcast with Ieshia Danielle",
            isPartOf: { "@id": "https://iamieshiadanielle.com/#website" },
            about: { "@id": "https://iamieshiadanielle.com/#person" },
            inLanguage: "en-US",
          },
          {
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: "https://iamieshiadanielle.com",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Podcast",
                item: "https://iamieshiadanielle.com/podcast",
              },
            ],
          },
          {
            "@type": "PodcastSeries",
            "@id": "https://iamieshiadanielle.com/podcast#series",
            name: "Between Me & You",
            description:
              "Honest conversations about emotions, unspoken trauma, healing, identity, and the God who meets us in the middle of it all.",
            url: "https://iamieshiadanielle.com/podcast",
            author: { "@id": "https://iamieshiadanielle.com/#person" },
            sameAs: [
              "https://youtube.com/@imieshiadanielle",
              "https://podcasts.apple.com/us/podcast/between-me-and-you-with-ieshia-danielle/id1736888997",
              "https://open.spotify.com/show/4P3vPSPtMLpDU1ERZ17pfVT",
            ],
          },
          {
            "@type": "VideoObject",
            name: "Love-Bombed By My Ex-Husband | Ep. 19 w/ Charlotte Taylor",
            description:
              "A raw, eye-opening story of being love-bombed, emotional manipulation, and how God restored her self-worth.",
            contentUrl: "https://www.youtube.com/watch?v=vmtLdHDA_UE",
            embedUrl: "https://www.youtube.com/embed/vmtLdHDA_UE",
            thumbnailUrl: "https://i.ytimg.com/vi/vmtLdHDA_UE/hqdefault.jpg",
            uploadDate: "2024-08-11",
            partOfSeries: {
              "@id": "https://iamieshiadanielle.com/podcast#series",
            },
          },
          {
            "@type": "VideoObject",
            name: "I Was Kicked Out Of Church | S3 EP. 2 w/ Alandria Simmons",
            description:
              "Surviving rejection in religious spaces and finding true intimacy with Jesus beyond legalism.",
            contentUrl: "https://www.youtube.com/watch?v=z1a9q9MAL-k",
            embedUrl: "https://www.youtube.com/embed/z1a9q9MAL-k",
            thumbnailUrl: "https://i.ytimg.com/vi/z1a9q9MAL-k/hqdefault.jpg",
            uploadDate: "2026-03-16",
            partOfSeries: {
              "@id": "https://iamieshiadanielle.com/podcast#series",
            },
          },
          {
            "@type": "VideoObject",
            name: "I Know For A Fact That God Is Real | Ep. 34 w/ First Lady Sarah Jones",
            description:
              "Miraculous encounters, undeniable faith testimonies, and trusting God through severe trials.",
            contentUrl: "https://www.youtube.com/watch?v=hTtM_PsUTTA",
            embedUrl: "https://www.youtube.com/embed/hTtM_PsUTTA",
            thumbnailUrl: "https://i.ytimg.com/vi/hTtM_PsUTTA/hqdefault.jpg",
            uploadDate: "2024-12-22",
            partOfSeries: {
              "@id": "https://iamieshiadanielle.com/podcast#series",
            },
          },
          {
            "@type": "VideoObject",
            name: "Adopted At Birth | Ep. 31 w/ Jasmine Lavender",
            description:
              "Uncovering root identity issues, adoption wounds, and discovering true belonging in God's family.",
            contentUrl: "https://www.youtube.com/watch?v=8B-j8B3LacA",
            embedUrl: "https://www.youtube.com/embed/8B-j8B3LacA",
            thumbnailUrl: "https://i.ytimg.com/vi/8B-j8B3LacA/hqdefault.jpg",
            uploadDate: "2024-11-17",
            partOfSeries: {
              "@id": "https://iamieshiadanielle.com/podcast#series",
            },
          },
          {
            "@type": "VideoObject",
            name: "I Know The Feelings I Have Are Wrong | Ep. 27 w/ Maxine Tarbin",
            description:
              "Surrendering unhealthy emotional attachments and aligning your heart with God's word.",
            contentUrl: "https://www.youtube.com/watch?v=QmsKi6iOSSA",
            embedUrl: "https://www.youtube.com/embed/QmsKi6iOSSA",
            thumbnailUrl: "https://i.ytimg.com/vi/QmsKi6iOSSA/hqdefault.jpg",
            uploadDate: "2024-10-13",
            partOfSeries: {
              "@id": "https://iamieshiadanielle.com/podcast#series",
            },
          },
          {
            "@type": "VideoObject",
            name: "Rejected Because of This | Ep. 18 w/ Dr. Racheal Butler",
            description:
              "Overcoming rejection wounds, medical challenges, and finding your unshakeable identity in Christ.",
            contentUrl: "https://www.youtube.com/watch?v=HSRNWF1kKC0",
            embedUrl: "https://www.youtube.com/embed/HSRNWF1kKC0",
            thumbnailUrl: "https://i.ytimg.com/vi/HSRNWF1kKC0/hqdefault.jpg",
            uploadDate: "2024-08-04",
            partOfSeries: {
              "@id": "https://iamieshiadanielle.com/podcast#series",
            },
          },
        ]}
      />
      <div className="min-h-screen bg-[#FAF6F0] text-[#380C1E] flex flex-col font-sans">
        <Header />

        <main className="flex-1">
          {/* Hero */}
          <section className="bg-[#F5E9EC] py-16 md:py-24">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-7 space-y-6">
                <p className="text-xs font-semibold text-[#9E1B50] uppercase tracking-widest italic">
                  Between Me & You
                </p>
                <h1 className="font-serif sm:text-5xl lg:text-6xl font-normal text-[#380C1E] leading-tight text-xl mt-[px] mb-[px]">
                  The conversations women of faith are carrying but rarely say
                  out loud.
                </h1>
                <p className="text-[#380C1E]/80 font-light leading-relaxed text-base mt-[-2px] mb-[-2px]">
                  Honest conversations about emotions, unspoken trauma, healing,
                  identity, and the God who meets us in the middle of it all.
                </p>

                <div className="flex flex-wrap gap-4 pt-4">
                  <a
                    href="https://youtube.com/@imieshiadanielle"
                    target="_blank"
                    rel="noreferrer"
                    className="bg-[#4A0E26] hover:bg-[#330A1F] text-white font-bold uppercase text-[11px] tracking-wider px-8 py-4 rounded-full shadow-sm transition-all inline-block"
                  >
                    Watch on YouTube →
                  </a>
                  <a
                    href="#latest"
                    className="bg-transparent border border-[#4A0E26] text-[#4A0E26] hover:bg-[#4A0E26] hover:text-white font-bold uppercase text-[11px] tracking-wider px-8 py-4 rounded-full transition-all inline-block"
                  >
                    Listen On the Go →
                  </a>
                </div>
              </div>

              <div className="lg:col-span-5">
                <div className="rounded-[2.5rem] overflow-hidden bg-neutral-200 border border-[#E8DEC8] shadow-md aspect-square">
                  <img
                    src="https://drive.google.com/thumbnail?id=1OWUsgK-syRGB8YS1pmfNq5bX_qreJmd2&sz=w1800"
                    alt="Between Me & You podcast studio with Ieshia Danielle"
                    className="w-full h-full object-cover object-center"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Fresh from the Mic - Most Recent Conversation */}
          <section
            id="latest"
            className="bg-[#4A0E26] py-16 md:py-24 text-white"
          >
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <p className="text-[10px] font-semibold text-white/70 uppercase tracking-widest mb-2">
                Fresh from the Mic
              </p>
              <h2 className="font-serif text-3xl md:text-5xl font-normal text-white mb-8">
                The Most Recent Conversation
              </h2>

              <div className="w-full max-w-[900px] mx-auto aspect-video bg-black rounded-3xl overflow-hidden shadow-2xl mb-8 border border-white/20">
                <iframe
                  className="w-full h-full"
                  src={`https://www.youtube.com/embed/${activeRecentEpisode.youtubeVideoId}?autoplay=0`}
                  title={activeRecentEpisode.episodeTitle}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>

              <div className="max-w-2xl mx-auto mb-8 space-y-2">
                <h3 className="font-serif text-xl sm:text-2xl text-white font-normal">
                  {activeRecentEpisode.episodeTitle}
                </h3>
                <p className="text-sm italic text-white/80">
                  "{activeRecentEpisode.quote}"
                </p>
              </div>

              <div className="flex flex-wrap justify-center gap-4">
                <a
                  href={activeRecentEpisode.youtubeUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="bg-transparent border border-white text-white hover:bg-white hover:text-[#4A0E26] font-bold uppercase text-[11px] tracking-wider px-8 py-3.5 rounded-full transition-all"
                >
                  Watch on YouTube ↗
                </a>
                <a
                  href="https://www.youtube.com/@imieshiadanielle"
                  target="_blank"
                  rel="noreferrer"
                  className="bg-[#9E1B50] hover:bg-[#801340] text-white font-bold uppercase text-[11px] tracking-wider px-8 py-3.5 rounded-full transition-all shadow-md"
                >
                  See All Episodes →
                </a>
              </div>
            </div>
          </section>

          {/* Featured Episodes Grid with Categories */}
          <FeaturedEpisodes
            episodes={episodes}
            onSelectEpisode={handleSelectEpisode}
          />

          {/* Be A Guest Testimony Form */}
          <PodcastGuestForm />

          {/* Listen and Connect */}
          <section
            id="listen-platforms"
            className="bg-[#380C1E] py-16 md:py-24 text-white"
          >
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
              <div className="space-y-2 text-center md:text-left">
                <p className="text-[10px] font-semibold text-white/70 uppercase tracking-widest">
                  Watch. Listen. Stay connected.
                </p>
                <h2 className="font-serif text-3xl md:text-5xl font-normal text-white">
                  Choose your platform.
                </h2>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <a
                  href="https://youtube.com/@imieshiadanielle"
                  target="_blank"
                  rel="noreferrer"
                  className="bg-white/10 hover:bg-white/20 rounded-2xl px-6 py-5 flex items-center justify-between transition-colors group border border-white/10"
                >
                  <span className="font-semibold text-sm text-white">
                    Watch on YouTube
                  </span>
                  <span className="text-white/80 font-bold text-sm group-hover:translate-x-0.5 transition-transform">
                    →
                  </span>
                </a>

                <a
                  href="https://podcasts.apple.com/us/podcast/between-me-and-you-with-ieshia-danielle/id1736888997"
                  target="_blank"
                  rel="noreferrer"
                  className="bg-white/10 hover:bg-white/20 rounded-2xl px-6 py-5 flex items-center justify-between transition-colors group border border-white/10"
                >
                  <span className="font-semibold text-sm text-white">
                    Listen on Apple Podcasts
                  </span>
                  <span className="text-white/80 font-bold text-sm group-hover:translate-x-0.5 transition-transform">
                    →
                  </span>
                </a>

                <a
                  href="https://open.spotify.com/show/4P3vPSPtMLpDU1ERZ17pfVT"
                  target="_blank"
                  rel="noreferrer"
                  className="bg-white/10 hover:bg-white/20 rounded-2xl px-6 py-5 flex items-center justify-between transition-colors group border border-white/10"
                >
                  <span className="font-semibold text-sm text-white">
                    Listen on Spotify
                  </span>
                  <span className="text-white/80 font-bold text-sm group-hover:translate-x-0.5 transition-transform">
                    →
                  </span>
                </a>

                <a
                  href="https://www.instagram.com/imieshiadanielle"
                  target="_blank"
                  rel="noreferrer"
                  className="bg-white/10 hover:bg-white/20 rounded-2xl px-6 py-5 flex items-center justify-between transition-colors group border border-white/10"
                >
                  <span className="font-semibold text-sm text-white">
                    Follow Ieshia on Instagram
                  </span>
                  <span className="text-white/80 font-bold text-sm group-hover:translate-x-0.5 transition-transform">
                    →
                  </span>
                </a>

                <Link
                  to="/shop"
                  className="bg-white/10 hover:bg-white/20 rounded-2xl px-6 py-5 flex items-center justify-between transition-colors group border border-white/10"
                >
                  <span className="font-semibold text-sm text-white">
                    Shop tools and resources
                  </span>
                  <span className="text-white/80 font-bold text-sm group-hover:translate-x-0.5 transition-transform">
                    →
                  </span>
                </Link>

                <Link
                  to="/book"
                  className="bg-white/10 hover:bg-white/20 rounded-2xl px-6 py-5 flex items-center justify-between transition-colors group border border-white/10"
                >
                  <span className="font-semibold text-sm text-white">
                    Book Ieshia to speak
                  </span>
                  <span className="text-white/80 font-bold text-sm group-hover:translate-x-0.5 transition-transform">
                    →
                  </span>
                </Link>
              </div>
            </div>
          </section>

          {/* Photo Gallery */}
          <PhotoGallery />

          <PrayerForm />
        </main>

        <Footer />
      </div>
    </>
  );
};

export default Podcast;
